<template>
    <!-- Without a date there is nothing to resolve a camera set as of, so the whole block stays out
         of the layout rather than rendering an explanation of its own absence. -->
    <div v-if="request" class="rounded-lg border border-gray-200 bg-white p-4">
        <h3 class="text-[10px] font-bold uppercase tracking-[0.5px] text-gray-400">
            {{ $t('views.monitoring.productionGroups.modals.manageProductionGroup.cameras.title') }}
            <span v-if="cameras.length">({{ cameras.length }})</span>
        </h3>
        <!-- One fixed floor for every state, so the timeline chart below cannot be pushed around
             while the camera set resolves. -->
        <div class="mt-1 flex min-h-10 items-center">
            <Spinner v-if="isPending" class="h-4 w-4" />
            <p v-else-if="!cameras.length" class="text-xs italic text-gray-400">
                {{ $t('views.monitoring.productionGroups.modals.manageProductionGroup.cameras.empty') }}
            </p>
            <!-- Clipped to two lines: the count in the heading stays accurate whether or not the
                 names themselves fit. -->
            <p v-else class="line-clamp-2 text-sm leading-5 text-gray-700">{{ cameraNames }}</p>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import Spinner from '@/components/ui/Spinner.vue';
import { useSectionCameras, type SectionCamerasRequest } from '@/plugins/store/actions/queries/production-groups';
import { cameraLabel } from './cameraIdentity';

// Read-only list of the cameras mounted on a Section, as of the start date currently entered in the
// manage-Production Group form. Owns its own read: the date it displays for is the only input it needs,
// and nothing here feeds back into the form.
const props = defineProps<{
    sectionId: number | null;
    // Date-only "yyyy-MM-dd", or null while the form has no start date.
    date: string | null;
}>();

const request = computed<SectionCamerasRequest | null>(() => (props.sectionId !== null && props.date ? { sectionId: props.sectionId, date: props.date } : null));

const { data, isPending } = useSectionCameras(request);

const cameras = computed(() => data.value ?? []);
// Through the shared label rather than the raw name: a Sensor with no name of its own would
// otherwise contribute an empty string to the join and read as a missing entry between two commas,
// so the label falls back to the camera's position in this list. That position is this list's own -
// resolved as of one calendar date - so an unnamed camera's label here need not match the one a
// chart gives it over a longer window (see cameraSeries.ts).
const cameraNames = computed(() => cameras.value.map((camera, index) => cameraLabel(camera, index)).join(', '));
</script>
